import { useCallback, useSyncExternalStore } from 'react'

import Axios from '../networking/intraceptor'
import * as path from '../networking/urlEndPoints'
import { authToken } from '../storage/authToken'

/**
 * ADMIN SCREEN PRIVACY ("hide my admin menus"), toggled by pressing F9 five
 * times. See AdminStealthManager for the key handling.
 *
 * WHAT IT DOES: while `hidden` is true, the portal renders an admin's
 * navigation as if they were an ordinary team member. The admin-only tabs,
 * sidebar entries and dropdown items are not drawn, and the admin-only routes
 * redirect to the dashboard.
 *
 * HOW IT DOES IT — deliberately with NO new visibility rules. Every admin-only
 * item in the portal is already gated on `adminModule === 1`, so this feature
 * reports an EFFECTIVE adminModule of 0 while hidden, at the two places that
 * read it (allSelectors' `adminModule` and useSuperNovaAccess). Every existing
 * check then hides its own item, and a hidden admin's portal is produced by
 * the same code path that serves normal users every day. Nothing else in the
 * app had to learn about this feature.
 *
 * WHAT IT IS NOT: this is presentation only. It hides menus; it revokes
 * nothing. The backend's authorisation is untouched and remains the only
 * security boundary.
 *
 * FAIL-SAFE CONTRACT (the mirror image of useMyModules' fail-open): on ANY
 * failure — an old backend that 404s the endpoint, a 500, a network error, no
 * token, a non-admin, an unreadable response — `hidden` is FALSE. A bug in
 * this feature can only ever leave the portal exactly as it is today; it can
 * never hide a menu by accident, and it can never hide anything from a user
 * who is not an admin.
 */

// Mirrors the server value so a reload does not flash the admin menus on
// screen before the request comes back. The server remains the source of
// truth: this is overwritten by every answer, and it is per-browser only,
// which is why the state is also stored against the user's account.
const STORAGE_KEY = 'oatrx.adminUiHidden'

const readMirror = () => {
	try {
		return window.localStorage.getItem(STORAGE_KEY) === '1'
	} catch (error) {
		// Private mode / blocked storage. Not an error: just no mirror.
		return false
	}
}

const writeMirror = (hidden) => {
	try {
		if (hidden) window.localStorage.setItem(STORAGE_KEY, '1')
		else window.localStorage.removeItem(STORAGE_KEY)
	} catch (error) {}
}

/**
 * One shared state for the whole app. `useCustomSelectors` is called from
 * dozens of components, so every one of them subscribes here; a module-level
 * value with a subscriber set keeps that to a single source and a single
 * request, exactly like useMyModules.
 *
 * INVARIANT: `hidden` is always the EFFECTIVE answer — it is resolved against
 * `canHide` at the point it is set, never by the consumers. That keeps the
 * question every caller asks ("hide the admin navigation?") to a single field
 * with no second condition to forget.
 *
 * `canHide` stays strictly authoritative (false until the backend confirms
 * it), because it decides whether the F9 listener is attached at all.
 */
let state = { hidden: false, canHide: false, loaded: false }
const listeners = new Set()

const emit = () => {
	// Copied before iterating: a listener may unsubscribe while we notify.
	Array.from(listeners).forEach((listener) => {
		try {
			listener()
		} catch (error) {
			// One bad consumer must never stop the others updating.
		}
	})
}

const setState = (next) => {
	if (state.hidden === next.hidden
		&& state.canHide === next.canHide
		&& state.loaded === next.loaded) return
	state = next
	emit()
}

const subscribe = (listener) => {
	// Opened here rather than on the first toggle: a tab must be listening
	// from the moment it mounts, including one that never toggles anything.
	openChannel()
	listeners.add(listener)
	return () => { listeners.delete(listener) }
}

const getSnapshot = () => state

// Token-keyed, like useMyModules: logout (token gone) and a fresh login (new
// token) both produce a different key, so neither can be served a stale answer.
let inFlight = null
let inFlightToken = null

/**
 * CROSS-TAB SYNC.
 *
 * Every browser tab runs its own copy of the app with its own memory. A tab
 * that was already open resolved this setting when it loaded and would keep
 * drawing that answer until it was refreshed — so toggling in one tab left the
 * menus on screen in another. For a privacy feature that is the wrong
 * behaviour: it has to take effect everywhere at once.
 *
 * Same mechanism the portal already uses for logout (see actions/main/logout):
 * a BroadcastChannel carrying one deliberate message per toggle.
 *
 * DELIBERATELY NOT the `storage` event, although the mirror write would fire it
 * for free. That event cannot tell a real toggle from the mirror being cleared
 * by a failed request or a logout, so a fetch failure in one tab would unhide
 * another. This channel only ever carries a toggle a person actually made.
 */
const CHANNEL_NAME = 'oatrx-admin-stealth'

// null = not opened yet, false = unavailable in this browser.
let channel = null

const closeChannel = () => {
	if (channel) {
		try { channel.close() } catch (error) {}
	}
	channel = null
}

/**
 * Apply a toggle made in ANOTHER tab. It only updates this tab's view — it
 * never writes back to the server (the tab that made the change already did)
 * and never re-broadcasts, so two tabs cannot bounce a message between them.
 */
const applyRemoteHidden = (next) => {
	// A tab that has not yet confirmed the user may use the feature ignores
	// the message. It resolves the authoritative answer on load anyway, so
	// nothing is lost — and the invariant that `hidden` implies `canHide`
	// cannot be broken from outside this tab.
	if (!state.canHide) return

	const hidden = next === true
	writeMirror(hidden)
	setState({ hidden, canHide: state.canHide, loaded: state.loaded })
}

const openChannel = () => {
	if (channel !== null) return channel

	try {
		channel = typeof BroadcastChannel !== 'undefined'
			? new BroadcastChannel(CHANNEL_NAME)
			: false
	} catch (error) {
		// Unsupported or blocked. Not an error: the feature simply keeps the
		// behaviour it had before this sync existed — each tab resolves the
		// setting when it loads.
		channel = false
	}

	if (channel) {
		channel.onmessage = (event) => {
			const message = event?.data
			if (!message || message.channel !== CHANNEL_NAME) return
			// Same browser, so the same session cookie — but check anyway, so a
			// message left over from a previous login cannot be applied.
			if (message.token !== authToken()) return
			applyRemoteHidden(message.hidden)
		}
	}

	return channel
}

const broadcastHidden = (hidden) => {
	const active = openChannel()
	if (!active) return

	try {
		active.postMessage({ channel: CHANNEL_NAME, hidden, token: authToken() })
	} catch (error) {
		// A failed broadcast must never fail the toggle itself: this tab is
		// already updated and the server already has the value.
	}
}

/**
 * Forget the cached answer. Called on logout so the next user cannot inherit
 * the previous one's state, even for a moment.
 */
export const resetAdminStealthCache = () => {
	inFlight = null
	inFlightToken = null
	state = { hidden: false, canHide: false, loaded: false }
	writeMirror(false)
	closeChannel()
	emit()
}

/**
 * Ask the backend for this user's stored setting. Resolved before the
 * dashboard opens on login (see authActions) so the admin menus cannot appear
 * and then vanish.
 */
export const primeAdminStealth = () => {
	const token = authToken()

	// PRE-LOGIN: never fire this without a token. The response interceptor
	// turns any 401 into "clear everything and bounce to /", which on the
	// login page means a wiped session. No token simply means "not logged in".
	if (!token) {
		resetAdminStealthCache()
		return Promise.resolve(state)
	}

	if (inFlight && inFlightToken === token) return inFlight

	inFlightToken = token
	// Apply the mirrored value immediately so a hidden admin's menus never
	// flash on screen during a hard refresh while this request is in flight.
	//
	// It is applied WITHOUT waiting to confirm `canHide` on purpose. The
	// mirror is only ever written for an admin who chose to hide, and the
	// worst case if the browser is now serving somebody else is that a user
	// with no admin menus briefly has no admin menus. The authoritative
	// answer below replaces it either way.
	setState({ hidden: readMirror(), canHide: false, loaded: false })

	inFlight = Axios.get(path.pharmacy_user_ui_visibility, {
		// The current value must always be resolved from the database after a
		// hard refresh. Do not let an intermediary reuse an earlier answer.
		headers: { 'Cache-Control': 'no-cache' },
	})
		.then((response) => {
			const body = response?.data
			const data = (body && typeof body.is_admin !== 'undefined') ? body : body?.data

			if (!data || typeof data.is_admin === 'undefined') {
				// Unknown shape (an older backend) -> visible, today's portal.
				writeMirror(false)
				setState({ hidden: false, canHide: false, loaded: true })
				return state
			}

			// WHO MAY USE THE SHORTCUT. Both roles carry something on screen
			// that exists only because of the role — an admin has the Team
			// menu and the rest of the admin navigation, a pharmacist has the
			// pharmacist navigation and the pre-recorded delivery-call clip —
			// so both can conceal it. `can_hide` answers exactly that.
			//
			// A backend from before pharmacists were included does not send
			// the field; `is_admin` is then read instead, which is precisely
			// that backend's answer. Nobody gains access from the fallback.
			const canHide = typeof data.can_hide !== 'undefined'
				? data.can_hide === true
				: data.is_admin === true
			// Belt and braces alongside the same check on the server: somebody
			// who may not use the feature can never be hidden, whatever the
			// stored value says.
			const hidden = canHide && data.admin_ui_hidden === true

			writeMirror(hidden)
			setState({ hidden, canHide, loaded: true })
			return state
		})
		.catch(() => {
			// FAIL-SAFE: any failure leaves the portal exactly as it is today.
			writeMirror(false)
			setState({ hidden: false, canHide: false, loaded: true })
			return state
		})

	return inFlight
}

/**
 * Flip the setting and persist it.
 *
 * The local state changes FIRST and is not rolled back if the save fails. That
 * is deliberate, and it matters most when UNHIDING: an admin who wants their
 * menus back must get them back immediately, even with the network down. The
 * server catches up on the next successful toggle.
 */
export const setAdminStealthHidden = (hidden) => {
	if (!state.canHide) return Promise.resolve(state)

	const next = hidden === true
	writeMirror(next)
	setState({ hidden: next, canHide: state.canHide, loaded: state.loaded })
	// Before the request, so the other tabs change with this one rather than
	// waiting on the network — and still change if the save fails.
	broadcastHidden(next)

	return Axios.post(path.pharmacy_user_ui_visibility, { hidden: next })
		.then(() => state)
		.catch(() => state)
}

/**
 * `{ hidden, canHide, loaded, toggle }` for the current user.
 *
 * Anyone who may not use the feature always reads `hidden: false`, so no
 * consumer needs its own role check before using it.
 */
const useAdminStealth = () => {
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

	const toggle = useCallback(() => setAdminStealthHidden(!getSnapshot().hidden), [])

	return {
		hidden: snapshot.hidden,
		canHide: snapshot.canHide,
		loaded: snapshot.loaded,
		toggle,
	}
}

/**
 * The single question the rest of the portal asks: should this user's
 * admin-only navigation be hidden right now?
 */
export const useAdminStealthHidden = () => {
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
	return snapshot.hidden
}

export default useAdminStealth
